import { getCurrencySymbol } from "@redotech/money/currencies";
import { useHandler } from "@redotech/react-util/hook";
import { getShopCurrencyWithDefault } from "@redotech/redo-billing-common/team-currency";
import TrashIcon from "@redotech/redo-icons/trash-03.svg";
import { getResource } from "@redotech/redo-model/localization/resource";
import { RegistrationRewardType } from "@redotech/redo-return-warranties-common/extended-warranty";
import {
  PurchaseChannel,
  RepairFeeCollectionStrategy,
  RetailAction,
} from "@redotech/redo-return-warranties-common/warranties";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTagInputField } from "@redotech/redo-web/arbiter-components/input/redo-tag-input-field";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Autocomplete } from "@redotech/redo-web/autocomplete";
import { Card } from "@redotech/redo-web/card";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import PlusIcon from "@redotech/redo-web/icon-old/plus.svg";
import { LabeledInput, LabelTheme } from "@redotech/redo-web/labeled-input";
import {
  FormSelectDropdown,
  SelectDropdown,
} from "@redotech/redo-web/select-dropdown";
import { FormSwitch, Switch } from "@redotech/redo-web/switch";
import { FormTextInput, TextInput } from "@redotech/redo-web/text-input";
import { groupInput, input, InputSchema, listInput } from "@redotech/ui/form";
import { numberValidator } from "@redotech/ui/validators";
import { arrayEqual, stringEqual } from "@redotech/util/equal";
import { httpsUrlValidator } from "@redotech/web-util/form";
import classNames from "classnames";
import { memo, useContext, useState } from "react";
import { TeamContext } from "../../team";
import {
  CollectionAutocomplete,
  SingleCollectionAutocomplete,
} from "../returns-package-protection/collections-autocomplete";

// TODO: Move to experience design when we want to customize
const registrationRewardTypeToTitle: Record<RegistrationRewardType, string> = {
  [RegistrationRewardType.DISABLED]: "Disabled",
  [RegistrationRewardType.PRODUCT_DISCOUNT]: "Product discount",
  [RegistrationRewardType.REVIEW_DISCOUNT]: "Review discount",
  [RegistrationRewardType.EMAIL_OPT_IN_PRODUCT_DISCOUNT]:
    "Email opt-in product discount",
  [RegistrationRewardType.EMAIL_DISCOUNT]: "Email discount (deprecated)",
};

const purchaseChannelToTitle: Record<PurchaseChannel, string> = {
  [PurchaseChannel.ONLINE]: "Shopify",
  [PurchaseChannel.RETAIL]: "Retail",
};

const retailActionToTitle: Record<RetailAction, string> = {
  [RetailAction.REGISTER_PRODUCT]: "Register product",
  [RetailAction.FILE_CLAIM]: "File claim",
  [RetailAction.START_RETURN]: "Start return",
};

// TODO: Remove each type once they are implemented
const disabledRegistrationRewardTypes = [
  RegistrationRewardType.REVIEW_DISCOUNT,
];

export const repairPricingForm = groupInput(
  {
    minPrice: input<string>({ validator: numberValidator({ min: 0.01 }) }),
    maxPrice: input<string>({ validator: numberValidator({ min: 0.01 }) }),
    collectionHandle: input<string>(),
    collectionTitle: input<string>(),
  },
  {},
);

export const repairPricingDefault = {
  minPrice: "1",
  maxPrice: "1",
  collectionHandle: "",
  collectionTitle: "",
};

const purchaseChannelForm = groupInput(
  { displayName: input<string>(), purchaseChannel: input<PurchaseChannel>() },
  {},
);

const purchaseChannelFormDefault = {
  displayName: "Shopify store",
  purchaseChannel: PurchaseChannel.ONLINE,
};

const repairFeeCollectionStrategyToTitle: Record<
  RepairFeeCollectionStrategy,
  string
> = {
  [RepairFeeCollectionStrategy.INSTANT]: "Instant",
  [RepairFeeCollectionStrategy.DEFERRED]: "Deferred",
};

const repairFeeCollectionStrategyToDescription: Record<
  RepairFeeCollectionStrategy,
  string
> = {
  [RepairFeeCollectionStrategy.INSTANT]:
    "The repair invoice will come through as a paid draft order and credited to the merchant's bill.",
  [RepairFeeCollectionStrategy.DEFERRED]:
    "The repair invoice can be edited before being sent to the customer, but must be sent manually.",
};

const purchaseChannelRegistrationCollectionForm = groupInput(
  {
    collectionId: input<string | undefined>(),
    purchaseChannel: input<PurchaseChannel>(),
  },
  {},
);

const purchaseChannelRegistrationCollectionDefault = {
  collectionId: undefined,
  purchaseChannel: PurchaseChannel.RETAIL,
};

export const warrantiesForm = groupInput(
  {
    enabled: input<boolean>(),
    customersCanStartClaims: input<boolean>(),
    registrationEnabled: input<boolean>(),
    redirectRegistrationToRetailActions: input<boolean>(),
    useWTYPrefixForRetail: input<boolean>(),
    registrationRewardType: input<RegistrationRewardType>(),
    registrationRewardDiscountId: input<string | undefined>(),
    registrationCustomerTags: input<string[]>({
      equal: arrayEqual(stringEqual),
    }),
    repairFeeEnabled: input<boolean>(),
    repairFeeCollectionStrategy: input<RepairFeeCollectionStrategy>(),
    labelCoverageEnabled: input<boolean>(),
    portalFourRegistration: input<boolean>(),
    redirectPostRegistration: input<string>({
      validator: (value) => {
        if (value === "" || value === null) {
          return [];
        }
        return httpsUrlValidator(value);
      },
    }),
    sendDefaultEmail: input<boolean>(),
    triggerMarketingFlow: input<boolean>(),
    hideItemPricing: input<boolean>(),
    showOtherOption: input<boolean>(),
    cantFindItemButtonEnabled: input<boolean>(),
    eligibleUpsellTypes: input<PurchaseChannel[]>({
      equal: arrayEqual(stringEqual),
    }),
    enabledRetailActions: input<RetailAction[]>({
      equal: arrayEqual(stringEqual),
    }),
    purchaseChannels: listInput(
      () => purchaseChannelForm,
      () => purchaseChannelFormDefault,
      (_, index) => index,
    ),
    defaultRepairMinPrice: input<string>({
      validator: numberValidator({ min: 0.01 }),
    }),
    defaultRepairMaxPrice: input<string>({
      validator: numberValidator({ min: 0.01 }),
    }),
    repairPricingCustomCollections: input<string[]>({
      equal: arrayEqual(stringEqual),
    }),
    repairPricingRuleSet: listInput(
      () => repairPricingForm,
      () => repairPricingDefault,
      (repairPricing) => repairPricing.collectionHandle,
    ),
    warrantyRegistrationCollection: input<string | undefined>(),
    purchaseChannelRegistrationCollection: listInput(
      () => purchaseChannelRegistrationCollectionForm,
      () => purchaseChannelRegistrationCollectionDefault,
      (_, index) => index,
      {
        validator: (value) => {
          const errors: string[] = [];
          if (
            value.some(
              (collection) =>
                collection.purchaseChannel === undefined ||
                collection.collectionId === undefined,
            )
          ) {
            errors.push("A purchase channel and collection is required");
          }
          return errors;
        },
      },
    ),
  },
  {},
);

export type WarrantiesForm = InputSchema.Input<typeof warrantiesForm>;

export type WarrantiesValue = InputSchema.Value<typeof warrantiesForm>;

export const warrantiesDefault: WarrantiesValue = {
  enabled: false,
  defaultRepairMaxPrice: "1",
  defaultRepairMinPrice: "1",
  customersCanStartClaims: false,
  registrationEnabled: false,
  redirectRegistrationToRetailActions: false,
  useWTYPrefixForRetail: false,
  registrationRewardType: RegistrationRewardType.DISABLED,
  registrationRewardDiscountId: undefined,
  registrationCustomerTags: [],
  repairFeeEnabled: false,
  repairFeeCollectionStrategy: RepairFeeCollectionStrategy.DEFERRED,
  labelCoverageEnabled: false,
  sendDefaultEmail: true,
  triggerMarketingFlow: false,
  hideItemPricing: false,
  showOtherOption: true,
  cantFindItemButtonEnabled: false,
  eligibleUpsellTypes: [PurchaseChannel.RETAIL],
  enabledRetailActions: [RetailAction.REGISTER_PRODUCT],
  warrantyRegistrationCollection: undefined,
  purchaseChannelRegistrationCollection: [],
  repairPricingCustomCollections: [],
  repairPricingRuleSet: [],
  purchaseChannels: [
    { displayName: "Shopify store", purchaseChannel: PurchaseChannel.ONLINE },
  ],
  portalFourRegistration: true,
  redirectPostRegistration: "",
};

export const Warranties = memo(function Warranties({
  input,
}: {
  input: WarrantiesForm;
}) {
  const {
    enabled,
    registrationEnabled,
    redirectRegistrationToRetailActions,
    useWTYPrefixForRetail,
    repairFeeEnabled,
    repairFeeCollectionStrategy,
    customersCanStartClaims,
    purchaseChannelRegistrationCollection,
    defaultRepairMaxPrice,
    defaultRepairMinPrice,
    repairPricingCustomCollections,
    repairPricingRuleSet,
    registrationRewardType,
    registrationCustomerTags,
    portalFourRegistration,
    redirectPostRegistration,
    purchaseChannels,
    sendDefaultEmail,
    triggerMarketingFlow,
    hideItemPricing,
    showOtherOption,
    cantFindItemButtonEnabled,
    eligibleUpsellTypes,
    enabledRetailActions,
  } = input.inputs;

  const team = useContext(TeamContext);

  const allPurchaseChannels = Object.values(PurchaseChannel);

  const [
    registrationCustomerTagsInputValue,
    setRegistrationCustomerTagsInputValue,
  ] = useState("");
  const registrationCustomerTagsValue = registrationCustomerTags.value.map(
    (tag) => ({ text: tag, variant: "secondary" as const }),
  );

  const setRegistrationCustomerTags = (tags: string[]) => {
    registrationCustomerTags.setValue(tags);
  };

  const handleToggle = useHandler((value: boolean) => {
    enabled.setValue(value);
    if (!value) {
      repairFeeEnabled.setValue(false);
      customersCanStartClaims.setValue(false);
      registrationEnabled.setValue(false);
    }
  });

  const currencyDisplay = getCurrencySymbol(
    getShopCurrencyWithDefault({ team }),
  );

  const handleSetCustomCollections = useHandler(
    (collections: { id: string; title: string; handle: string }[]) => {
      repairPricingCustomCollections.setValue(
        collections.map((collection) => collection.handle),
      );
      repairPricingRuleSet.setValue(
        collections.map((collection) => ({
          minPrice: "1",
          maxPrice: "1",
          collectionHandle: collection.handle,
          collectionTitle: collection.title,
        })),
      );
    },
  );

  const handleAddPurchaseChannel = useHandler(() => {
    const existingPurchaseChannels = purchaseChannels.value;
    const newPurchaseChannel = {
      displayName: "",
      purchaseChannel: PurchaseChannel.ONLINE,
    };
    purchaseChannels.setValue([
      ...existingPurchaseChannels,
      newPurchaseChannel,
    ]);
  });

  const handleRemovePurchaseChannel = useHandler((index: number) => {
    purchaseChannels.setValue(
      purchaseChannels.value.filter((_, i) => i !== index),
    );
  });

  const handleAddPCRegistrationCollection = useHandler(() => {
    purchaseChannelRegistrationCollection.setValue([
      ...purchaseChannelRegistrationCollection.value,
      { collectionId: undefined, purchaseChannel: PurchaseChannel.RETAIL },
    ]);
  });

  const handleRemovePCRegistrationCollection = useHandler((index: number) => {
    purchaseChannelRegistrationCollection.setValue(
      purchaseChannelRegistrationCollection.value.filter((_, i) => i !== index),
    );
  });

  return (
    <Card title="Warranties">
      <section className={gridCss.grid}>
        <div className={classNames(gridCss.span12, gridCss.span6M)}>
          <LabeledInput
            description="Display the warranty flow to merchants"
            label="Warranty flow"
          >
            <Switch onChange={handleToggle} value={enabled.value} />
          </LabeledInput>
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6M)}>
          <FormSwitch
            input={customersCanStartClaims}
            label="Allows customers to start claims"
          >
            Show the '{getResource({ variant: "warranty" }).PORTAL_BUTTON_TEXT}'
            button on the customer portal.
          </FormSwitch>
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6M)}>
          <FormSwitch input={registrationEnabled} label="Warranty registration">
            Allow customers to register their warranties and submit claims on
            them.
          </FormSwitch>
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6M)}>
          <FormSwitch
            input={redirectRegistrationToRetailActions}
            label="Redirect registration to retail actions"
          >
            Redirect warranty registration to retail actions page when enabled.
          </FormSwitch>
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6M)}>
          <FormSwitch
            input={useWTYPrefixForRetail}
            label="Use WTY prefix for retail"
          >
            Use WTY prefix for retail warranty registrations when enabled.
          </FormSwitch>
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6M)}>
          <FormSwitch
            input={portalFourRegistration}
            label="Portal 4.0 registration"
          >
            Use the new portal 4.0 registration flow.
          </FormSwitch>
        </div>
        <div className={gridCss.span12}>
          <FormSelectDropdown
            input={registrationRewardType}
            label="Registration reward type"
            options={Object.values(RegistrationRewardType).filter(
              (type) => !disabledRegistrationRewardTypes.includes(type),
            )}
          >
            {(option) => registrationRewardTypeToTitle[option]}
          </FormSelectDropdown>
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6M)}>
          <FormSwitch
            input={sendDefaultEmail}
            label="Send default warranty email"
          >
            Send the default warranty registration confirmation email.
          </FormSwitch>
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6M)}>
          <FormSwitch
            input={triggerMarketingFlow}
            label="Trigger marketing flow"
          >
            Trigger marketing flow on warranty registration.
          </FormSwitch>
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6M)}>
          <FormSwitch
            input={hideItemPricing}
            label="Hide item pricing during registration"
          >
            Hide product pricing details in the warranty registration flow.
          </FormSwitch>
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6M)}>
          <RedoTagInputField
            description="These are the tags that will be added to customers created when they register a warranty."
            inputValue={registrationCustomerTagsInputValue}
            label="Registration customer tags"
            placeholder="Enter a tag"
            setInputValue={setRegistrationCustomerTagsInputValue}
            setTags={setRegistrationCustomerTags}
            size="lg"
            tags={registrationCustomerTagsValue}
          />
        </div>
        <div className={gridCss.span12}>
          <RedoTextInput
            description="If set, the customer will be redirected to this URL after registration."
            fullWidth
            label="Redirect post registration"
            placeholder="https://example.com"
            setValue={redirectPostRegistration.setValue}
            value={redirectPostRegistration.value ?? ""}
          />
        </div>
        <div className={gridCss.span12}>
          <LabeledInput
            description="Purchase channels eligible for extended warranty upsell"
            label="Eligible upsell types"
          >
            <Autocomplete
              getLabel={(option) => purchaseChannelToTitle[option]}
              keyFn={(option) => option}
              multiple
              options={Object.values(PurchaseChannel)}
              value={eligibleUpsellTypes.value}
              valueChange={eligibleUpsellTypes.setValue}
            >
              {(option) => purchaseChannelToTitle[option]}
            </Autocomplete>
          </LabeledInput>
        </div>
        <div className={gridCss.span12}>
          <LabeledInput
            description="Retail actions available to customers in the portal"
            label="Enabled retail actions"
          >
            <Autocomplete
              getLabel={(option) => retailActionToTitle[option]}
              keyFn={(option) => option}
              multiple
              options={Object.values(RetailAction)}
              value={enabledRetailActions.value}
              valueChange={enabledRetailActions.setValue}
            >
              {(option) => retailActionToTitle[option]}
            </Autocomplete>
          </LabeledInput>
        </div>

        <div className={gridCss.span12}>
          <strong>Purchase channels</strong>
        </div>
        {purchaseChannels.inputs?.map((purchaseChannel, index) => (
          <div className={gridCss.span12} key={index}>
            <Flex flexDirection="row" w="full">
              <Flex w="full">
                <TextInput
                  error={!!purchaseChannel.inputs.displayName.errors.length}
                  fullwidth
                  onChange={purchaseChannel.inputs.displayName.setValue}
                  placeholder="My shopify store"
                  value={purchaseChannel.inputs.displayName.value}
                />
              </Flex>
              <Flex>
                <SelectDropdown
                  options={Object.values(PurchaseChannel)}
                  value={purchaseChannel.inputs.purchaseChannel.value}
                  valueChange={purchaseChannel.inputs.purchaseChannel.setValue}
                >
                  {(option) => purchaseChannelToTitle[option]}
                </SelectDropdown>
              </Flex>
              <Flex align="center">
                <RedoButton
                  aria-label="Remove purchase channel"
                  disabled={purchaseChannels.inputs.length <= 1}
                  IconLeading={TrashIcon}
                  onClick={() => handleRemovePurchaseChannel(index)}
                  size="md"
                />
              </Flex>
            </Flex>
          </div>
        ))}
        <div className={gridCss.span12}>
          <Flex w="full">
            <RedoButton
              IconLeading={PlusIcon}
              onClick={handleAddPurchaseChannel}
              text="Add purchase channel"
            />
          </Flex>
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6M)}>
          <FormSwitch input={showOtherOption} label="Show 'Other' option">
            Show the 'Other' option as a purchase channel.
          </FormSwitch>
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6M)}>
          <FormSwitch
            input={input.inputs.labelCoverageEnabled}
            label="Use return coverage to cover warranty labels"
          >
            Allow return coverage to cover the cost of warranty labels.
          </FormSwitch>
        </div>
        <div className={gridCss.span12}>
          <strong>Purchase channel registration collections</strong>
        </div>
        {purchaseChannelRegistrationCollection.inputs?.map(
          (pcRegistrationCollection, index) => (
            <div className={gridCss.span12} key={index}>
              <Flex align="center" flexDirection="row" w="full">
                <Flex flexDirection="column" w="full">
                  <SelectDropdown
                    options={allPurchaseChannels}
                    value={
                      pcRegistrationCollection.inputs.purchaseChannel.value
                    }
                    valueChange={
                      pcRegistrationCollection.inputs.purchaseChannel.setValue
                    }
                  >
                    {(option) => purchaseChannelToTitle[option]}
                  </SelectDropdown>
                </Flex>
                <Flex flexDirection="column" w="full">
                  <SingleCollectionAutocomplete
                    keyFn={(collection) => collection.id}
                    setValue={
                      pcRegistrationCollection.inputs.collectionId.setValue
                    }
                    value={pcRegistrationCollection.inputs.collectionId.value}
                  />
                </Flex>
                <Flex align="center">
                  <RedoButton
                    aria-label="Remove registration collection"
                    IconLeading={TrashIcon}
                    onClick={() => handleRemovePCRegistrationCollection(index)}
                    size="md"
                  />
                </Flex>
              </Flex>
            </div>
          ),
        )}
        <div className={gridCss.span12}>
          <Flex w="full">
            <RedoButton
              disabled={!allPurchaseChannels.length}
              IconLeading={PlusIcon}
              onClick={handleAddPCRegistrationCollection}
              text="Add purchase channel registration collection"
            />
          </Flex>
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6M)}>
          <FormSwitch
            input={cantFindItemButtonEnabled}
            label="Enable 'Can't find your item?' button"
          >
            Show a &quot;Can&apos;t find your item?&quot; button on the product
            selection screen during warranty registration.
          </FormSwitch>
        </div>
        <div className={classNames(gridCss.span12)}>
          <Divider />
        </div>
        <div className={gridCss.span12}>
          <strong>Repair fees</strong>
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6M)}>
          <FormSwitch input={repairFeeEnabled} label="Repair fees enabled">
            Charge customers a flat rate for repairs.
          </FormSwitch>
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6M)}>
          <FormSelectDropdown
            description={
              repairFeeCollectionStrategyToDescription[
                repairFeeCollectionStrategy.value
              ]
            }
            input={repairFeeCollectionStrategy}
            label="Repair fee collection strategy"
            options={Object.values(RepairFeeCollectionStrategy)}
          >
            {(option) => repairFeeCollectionStrategyToTitle[option]}
          </FormSelectDropdown>
        </div>
        <div className={gridCss.span12}>
          <strong>Default pricing</strong>
        </div>
        <div className={classNames(gridCss.span6L)}>
          <FormTextInput
            input={defaultRepairMinPrice}
            label="Min price"
            labelTheme={LabelTheme.THIN}
            min={0.01}
            prefix={currencyDisplay}
            step={0.01}
            type="number"
          />
        </div>
        <div className={classNames(gridCss.span6L)}>
          <FormTextInput
            input={defaultRepairMaxPrice}
            label="Max price"
            labelTheme={LabelTheme.THIN}
            min={0.01}
            prefix={currencyDisplay}
            step={0.01}
            type="number"
          />
        </div>
        <div className={gridCss.span12}>
          <LabeledInput
            description="Select the collections that will have a custom repair fee."
            label="Custom collections"
          >
            <CollectionAutocomplete
              keyFn={(collection) => collection.handle}
              setValueCollections={handleSetCustomCollections}
              value={repairPricingCustomCollections.value ?? []}
            />
          </LabeledInput>
        </div>
        {repairPricingRuleSet.inputs?.map((repairPricing, index) => (
          <div className={gridCss.span12} key={index}>
            <div className={gridCss.grid}>
              <div className={gridCss.span12}>
                <strong>{repairPricing.value.collectionTitle}</strong>
              </div>
              <div className={classNames(gridCss.span12, gridCss.span6L)}>
                <FormTextInput
                  input={repairPricing.inputs.minPrice}
                  label="Min price"
                  labelTheme={LabelTheme.THIN}
                  min={0.01}
                  prefix={currencyDisplay}
                  step={0.01}
                  type="number"
                />
              </div>
              <div className={classNames(gridCss.span12, gridCss.span6L)}>
                <FormTextInput
                  input={repairPricing.inputs.maxPrice}
                  label="Max price"
                  labelTheme={LabelTheme.THIN}
                  min={0.01}
                  prefix={currencyDisplay}
                  step={0.01}
                  type="number"
                />
              </div>
            </div>
          </div>
        ))}
      </section>
    </Card>
  );
});
